/**
 * The second page of `tuning.ts` (which re-exports it, so everything still imports from
 * `./tuning`): rare gear (04 §6), searchlights (02 §9.2), mission 10's executioners, and the
 * numbers behind the round-two perks (04 §4). It imports nothing from `tuning.ts`: that file
 * re-exports this one, and a cycle would leave `secs` undefined while this module loads.
 */

import { TICK_RATE } from './types';

const secs = (s: number): number => Math.round(s * TICK_RATE);

/** Suppressed pistol, smoke grenades and wire cutters. NVG is presentation only. */
export const GEAR = {
  /** Semi-automatic: one round per press, this long between them. */
  pistolTicksPerShot: secs(0.25),
  pistolRange: 25,
  /** Degrees of spread (half-angle): a handgun, however steady the hand. */
  pistolSpreadStill: 2.2,
  pistolSpreadMoving: 5,
  /** A cough, heard like a footstep: men this close turn to look, and the garrison never hears of it. */
  pistolNoise: 6,
  /** Enough to drop any infantryman outright - dealt only to one who did not know the ranger was there. */
  pistolUnawareDamage: 99,
  /** A man at or past this detection knows something is up: the round is just a pistol round to him. */
  pistolAwareAt: 0.75,
  /** The cloud swells to `smokeRadius` over `smokeGrowTicks`, hangs, then thins to nothing. */
  smokeRadius: 6,
  smokeGrowTicks: secs(1),
  smokeHoldTicks: secs(18),
  smokeFadeTicks: secs(3),
  /** Thinner than this it hides nobody. */
  smokeMinRadius: 0.5,
  /** Cutting a wire run: within this of its footprint, holding still this long. Silent. */
  cutReach: 1.1,
  cutTicks: secs(4),
  /** Tag Recovery (04 §2.3): Interact this close to the field grave. */
  tagsReach: 2,
} as const;

/** What rare gear weighs on the ranger - the numbers the loadout screen charges (src/meta/content/economy.ts). */
export const GEAR_WEIGHT = { smoke: 1, pistol: 2, nvg: 1, wireCutters: 1, ghillie: 2 } as const;

export const SEARCHLIGHT = {
  /** Sweeps this arc, centred on the prop's yaw (0 = south, toward the drop band), at this rate. */
  arcDeg: 100,
  sweepDegPerSecond: 12,
  range: 32,
  halfAngleDeg: 9,
  /** The lamp sits on a pole: it looks down over low cover. */
  lampHeight: 3,
  /** Held in the beam this long, the crew calls it in: alert 1 at his position, the nearest man comes to look. */
  alarmTicks: secs(1.5),
} as const;

/** Mission 10 (03 §2): once the alarm is up - or the door is open - men are sent to kill the hostages. */
export const EXECUTION = {
  max: 2,
  /** Only men this close to the prison (or to a hostage in the open) are given the job. */
  recruitRadius: 30,
  /** Standing this close to a hostage in the open - or this close to the wall of his cell - he kills one... */
  reach: 3,
  cellReach: 1.6,
  /** ...this often. */
  everyTicks: secs(6),
} as const;

/** Round-two perks and traits (04 §4): the numbers their `SimMods` fields scale. */
export const PERKS = {
  /** Half-angle, degrees, of a LAW's wander at `lawDeviation` 1 (Bunker Buster sets it to 0). */
  lawDeviationDeg: 1.5,
  /** Salvager: what one body yields. */
  salvageMagazines: 1,
  salvageGrenades: 1,
  /** Mine Sense: metres off the sense radius while running - the perk's own "4 m (3 m while running)". */
  mineSenseRunPenalty: 1,
} as const;

/**
 * The Storm modifier (04 §7): "vision -50 % both sides, heavy wind, stamina drain +20 %". Until round
 * five nothing in the sim read the weather at all, so the card was worth its 1.25 reward for nothing,
 * and Cold Blooded and Woodsman - which exist to cancel the drain - cancelled nothing (ROUND5 §3.2).
 * `WEATHER_PENALTY` in src/meta/content/perks.ts takes `fatigue` from here so the two cannot drift.
 */
export const STORM = {
  /** Multiplier on every soldier's sighting range while it blows. */
  vision: 0.5,
  /** Extra fatigue per second of running, as a fraction: the same shape as the desert's heat. */
  fatigue: 0.2,
  /**
   * Lightning, thrown by a thunderstorm alone (02 §5). A storm halves what everyone can see; a stroke gives it all
   * back for an instant, so the cover the weather was lending you is not a thing you can lean on.
   * The window either side of a stroke, and how far apart the strokes run: a range, because a
   * metronome would be learnable and this must not be.
   */
  strikeTicks: secs(0.45),
  strikeEverySeconds: [9, 26] as const,
} as const;

/** The expansion's objective kinds and rules (10 §3). Owned by src/sim/objectives. */
export const EXPANSION = {
  /** Interact reach to the body of a loot target, and to a contact waiting to be met. */
  lootReach: 1.6,
  meetReach: 2.2,
  /** A search-party man this close to a captive who is still hidden (`held`) has found him. */
  foundRadius: 3,
  /** `strikeLands`: blast radius of each impact unless the rule gives one. */
  strikeRadius: 18,
  /** The pilot is hurt: he walks, never runs (the ranger walks at 2.2). */
  pilotSpeed: 1.8,
  /** `shootTheCaptive`: a guard within this of the man still at large does it. */
  shootRadius: 40,
  /** A defector will not move while gunfire has been heard in the last little while (`world.combatTicks`). */
  defectorFreezes: true,
  /** `targetFlees`: he is gone this close to the north edge (as the officer of mission 4). */
  fleeEdgeMargin: 4,
} as const;

/**
 * Vehicles (ROUND4 §1): machines that drive a route, carry men, run them over and leave the map.
 * Owned by src/sim/enemies/specials/vehicle.ts and src/sim/hazards/spans.ts.
 */
export const VEHICLES = {
  /** The hull's half-width, for movement: a lorry keeps to a 4.2 m road with this to spare on either side. */
  radius: 1.1,
  /**
   * The hull's half-LENGTH, per kind (FIELD's measurements of the models, 2026-09-21). The hull is a capsule
   * of `radius` down this line, turned by `facing`: what stops a 9.8 m locomotive is what its NOSE runs into,
   * and a man anywhere along it goes under it. Without this the train was a 2.2 m circle in a 9.8 m silhouette.
   */
  halfLength: { truck: 3.15, staffCar: 2.2, railEngine: 4.9, launcherTruck: 4.2 },
  /** Pushed this far off its line by something solid, it is blocked and stops (the men aboard get down). */
  pushTolerance: 0.06,
  /**
   * What it leaves behind, per kind: how far the wreck MODEL's debris actually reaches (FIELD's measurements).
   * One figure for all four stopped the player 0.65 m short of a staff car he could plainly walk round.
   */
  wreckRadius: { truck: 1.9, staffCar: 1.25, railEngine: 2.05, launcherTruck: 2 },
  /**
   * How tall the wreck MODEL actually stands, per kind: the top of each builder's bounding box, measured
   * (`fieldVehicles.test.ts` holds them to it). It decides what stops a bullet (`BULLET_BLOCK_HEIGHT`) and
   * what a man can see over. One flat figure of 2.2 was wrong both ways - a burnt saloon claimed 0.7 m of
   * cover it does not have, and rounds went straight through the top metre of a locomotive that is plainly
   * standing there.
   */
  wreckHeight: { truck: 2.65, staffCar: 1.5, railEngine: 3.15, launcherTruck: 2.29 },
  /** Rad/s the hull comes round; a waypoint this near counts as reached. */
  turnRate: 2.4,
  nodeReach: 1.5,
  /** Metres per second when a route does not say. */
  speed: 7,
  /** Armour: only a LAW, a bomb, a cook-off or a mine this close to the hull cracks it (a grenade never does). */
  killRadius: 3,
  /** A mine this close to the hull goes off under its wheels. */
  mineTrigger: 1.4,
  /** A man this close to a moving hull goes under it. */
  runOverRadius: 2,
  /** ...and so does the ranger, if it is doing at least this: two wounds, and knocked flat for this long. */
  runOverSpeed: 2,
  runOverWounds: 2,
  knockedFlatTicks: secs(1.2),
  /** It cannot run the same man (or the ranger) down twice inside this. */
  runOverEveryTicks: secs(1.5),
  /** Stopped dead this long, the men aboard get down and fight. */
  stalledTicks: secs(2.5),
  /** A car sent for one man waits while he is running to it from this far off (mission 29). */
  waitRadius: 34,
  /** Passengers get down this far out from the hull, and this near a `dismountAt` point. */
  dismountRing: 2.4,
  dismountReach: 3.5,
  /** A wrecked vehicle: this fraction of the men still aboard go up with it; the rest bail out wounded. */
  killedAboard: 0.5,
  /** The fallen span (ROUND4 §1): the rubble is this wide either side of the lane, and this high. */
  spanWreckHalfWidth: 2.8,
  spanWreckHeight: 1,
} as const;

/** Round three's integration fixes: weak sides, charges under the column, modifiers that had no effect (04 §7). */
export const INTEG3 = {
  /** Ticks between two "NO EFFECT" notices about the same prop (`PropSpec.weakSide`). */
  blastRefusedEveryTicks: secs(4),
  /**
   * `bombsHoldUntilMidnight.detonateUnder`: a man of the column inside the radius sets a charge off once he is as
   * near as he will come - within `detonateOnTop`, or no longer closing - so the blast catches the file, not its point.
   */
  detonateUnderRadius: 6,
  detonateOnTop: 2,
  /** Scattered Drop: the wind on a falling pod is multiplied by this, and each pod has a gust of half to all of `podGust` m/s. */
  scatteredDrift: 3,
  podGust: 6,
  /** Hot Extraction: the hover is this fraction of the usual, and this many men make for the pickup. */
  hotHoverFactor: 0.6,
  hotSquad: 4,
} as const;
